import React, {useEffect, useState} from "react";
import axios from "axios";
import Select from "react-select";
import DateTimePicker from "react-datetime-picker";
import {ClipLoader} from "react-spinners";
import {apiConfig} from "../../../../config";

// Статистика прозвона по сегментам (сайтам вакансий), с которых пришли номера.
// Номер с несколькими сайтами считается в каждом, в «Итого» — один раз, поэтому итог не равен сумме строк.

const DEFAULT_PROJECT = 'hr_uhead'

type Row = {
  segment: string | null
  name: string | null
  day: string | null
  base: number
  called: number
  answered: number
  talks: number
  leads: number
  recalls: number
  negatives: number
  dropped: number
  answering_machine: number
  hung_up: number
  no_status: number
  no_operator: number
  no_answer: number
  attempts: number
  talk_sec: number
}

type Column = {
  key: string
  title: string
  value: (row: Row) => number | null
  format: (row: Row) => string
  detail?: boolean
}

const ratio = (a: number, b: number) => b ? a / b : null
const percent = (a: number, b: number) => {
  if (!b) return '—'
  const value = 100 * a / b
  return value === 0 || value === 100 ? `${value}%` : `${value.toFixed(1).replace('.', ',')}%`
}
const number = (value: number) => value.toLocaleString('ru-RU')

const COLUMNS: Column[] = [
  {key: 'base', title: 'В базе', value: r => r.base, format: r => number(r.base)},
  {key: 'called', title: 'Обзвонено', value: r => r.called, format: r => number(r.called)},
  {key: 'answered', title: 'Дозвон', value: r => ratio(r.answered, r.called), format: r => percent(r.answered, r.called)},
  {key: 'talks', title: 'Живой разговор', value: r => r.talks, format: r => number(r.talks)},
  {key: 'leads', title: 'Лиды', value: r => r.leads, format: r => number(r.leads)},
  {key: 'lead_called', title: 'Лид от обзвон.', value: r => ratio(r.leads, r.called), format: r => percent(r.leads, r.called)},
  {key: 'lead_talks', title: 'Лид от разговоров', value: r => ratio(r.leads, r.talks), format: r => percent(r.leads, r.talks)},
  {key: 'recalls', title: 'Перезвон', value: r => r.recalls, format: r => number(r.recalls), detail: true},
  {key: 'negatives', title: 'Негатив', value: r => r.negatives, format: r => number(r.negatives), detail: true},
  {key: 'dropped', title: 'Сорвался', value: r => r.dropped, format: r => number(r.dropped), detail: true},
  {key: 'no_status', title: 'Разговор без статуса', value: r => r.no_status, format: r => number(r.no_status), detail: true},
  {key: 'answering_machine', title: 'АО / тишина', value: r => r.answering_machine, format: r => number(r.answering_machine), detail: true},
  {key: 'hung_up', title: 'Сбросил', value: r => r.hung_up, format: r => number(r.hung_up), detail: true},
  {key: 'no_operator', title: 'Без оператора', value: r => r.no_operator, format: r => number(r.no_operator), detail: true},
  {key: 'no_answer', title: 'Не дозвонились', value: r => r.no_answer, format: r => number(r.no_answer), detail: true},
  {key: 'attempts', title: 'Звонков', value: r => r.attempts, format: r => number(r.attempts), detail: true},
  {key: 'talk_min', title: 'Минут разговора', value: r => Math.round(r.talk_sec / 60), format: r => number(Math.round(r.talk_sec / 60)), detail: true},
]

const dayStart = (shiftDays: number) => {
  const date = new Date()
  date.setDate(date.getDate() + shiftDays)
  date.setHours(0, 0, 0, 0)
  return date
}

const weekStart = () => {
  const date = dayStart(0)
  date.setDate(date.getDate() - (date.getDay() + 6) % 7)
  return date
}

// кабинет шлёт локальное время с суффиксом Z, бэкенд отбрасывает зону
const toApiDate = (date: any) => {
  if (!date) return null
  const tzoffset = (new Date()).getTimezoneOffset() * 60000
  return (new Date(new Date(date).getTime() - tzoffset)).toISOString()
}

const formatDay = (day: string | null) => day ? day.split('-').reverse().join('.') : 'без звонков'

const buttonStyle = (active: boolean = false) => ({
  display: "flex",
  height: 36,
  paddingLeft: 12,
  paddingRight: 12,
  borderRadius: 5,
  justifyContent: "center",
  alignItems: 'center',
  borderWidth: 1,
  borderColor: "black",
  marginRight: 8,
  backgroundColor: active ? "white" : "",
})

const filterLabel = {marginRight: 10, whiteSpace: "nowrap" as const}

export const SegmentsStatComp = () => {
  const [projectsOptions, setProjectsOptions]: [any, any] = useState([])
  const [operatorsOptions, setOperatorsOptions]: [any, any] = useState([])
  const [segmentsOptions, setSegmentsOptions]: [any, any] = useState([])

  const [selectProjects, setSelectProjects]: [any, any] = useState([])
  const [selectOperators, setSelectOperators]: [any, any] = useState([])
  const [selectSegments, setSelectSegments]: [any, any] = useState([])

  const [startDate, setStartDate]: [any, any] = useState(weekStart())
  const [endDate, setEndDate]: [any, any] = useState(dayStart(1))

  const [byDay, setByDay] = useState(false)
  const [rawNames, setRawNames] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [sort, setSort]: [any, any] = useState({key: 'called', desc: true})

  const [response, setResponse]: [any, any] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError]: [any, any] = useState(null)

  const authConfig = () => ({headers: {Authorization: `Bearer ${localStorage.getItem('token_caltatToki')}`}})

  const getStat = async (projects: any[] = selectProjects) => {
    setLoading(true)
    setError(null)
    try {
      const result = await axios({
        method: 'get',
        url: `${apiConfig.url}/statistic/segments`,
        ...authConfig(),
        params: {
          project_id: projects.map((item: any) => item.value),
          user_id: selectOperators.map((item: any) => item.value),
          segment: selectSegments.map((item: any) => item.value),
          date_from: toApiDate(startDate),
          date_to: toApiDate(endDate),
          others_min: 0,
          by_day: byDay,
        },
        paramsSerializer: {indexes: null},
      })
      setResponse(result.data)
      setSegmentsOptions(result.data.segments.map((item: any) => ({
        value: item.segment ?? '',
        label: `${item.segment ? item.name : 'Без сайта'} (${number(item.phones)})`,
      })))
    } catch (e: any) {
      console.error('error', e)
      setError(e?.response?.status === 403 ? 'Статистика по сегментам доступна только администратору' : 'Не удалось загрузить статистику')
    }
    setLoading(false)
  }

  const init = async () => {
    let projects: any[] = []
    try {
      const [responseProjects, responseUsers] = await Promise.all([
        axios({method: 'get', url: `${apiConfig.url}/project/list`, ...authConfig()}),
        axios({method: 'get', url: `${apiConfig.url}/auth/list`, ...authConfig()}),
      ])
      projects = responseProjects.data.map((item: any) => ({value: item.id, label: item.name}))
      setProjectsOptions(projects)
      setOperatorsOptions(responseUsers.data.map((item: any) => ({value: item.id, label: item.name})))
    } catch (e) {
      console.error('error', e)
    }
    const defaultProjects = projects.filter((item: any) => item.label === DEFAULT_PROJECT)
    setSelectProjects(defaultProjects)
    getStat(defaultProjects)
  }

  useEffect(() => {
    init()
  }, [])

  const rowName = (row: Row) => {
    if (row.segment === null) return 'Без сайта в базе сегментов'
    return rawNames ? row.segment : (row.name || row.segment)
  }

  const columns = COLUMNS.filter(column => showDetails || !column.detail)

  const sortRows = (rows: Row[]) => {
    const column = COLUMNS.find(item => item.key === sort.key)
    const pinned = (row: Row) => row.segment === null ? 1 : 0
    return [...rows].sort((a, b) => {
      if (pinned(a) !== pinned(b)) return pinned(a) - pinned(b)
      if (sort.key === 'segment') return rowName(a).localeCompare(rowName(b)) * (sort.desc ? -1 : 1)
      const va = column?.value(a) ?? -1
      const vb = column?.value(b) ?? -1
      if (va !== vb) return (va - vb) * (sort.desc ? -1 : 1)
      return b.talks - a.talks
    })
  }

  // при разбивке по дням — блоки по дням, в каждом свои строки и итог дня
  const blocks: { day: string | null, rows: Row[], total: Row | null }[] = []
  if (response) {
    const rows: Row[] = response.rows
    const days: (string | null)[] = byDay
      ? Array.from(new Set<string | null>(rows.map(row => row.day))).sort((a, b) => (a ?? '9999').localeCompare(b ?? '9999'))
      : [null]
    for (const day of days) {
      blocks.push({
        day,
        rows: sortRows(byDay ? rows.filter(row => row.day === day) : rows),
        total: response.totals.find((row: Row) => row.day === day) || null,
      })
    }
  }

  const downloadCsv = () => {
    const header = [...(byDay ? ['Дата'] : []), 'Сегмент', ...columns.map(column => column.title)]
    const lines = [header]
    for (const block of blocks) {
      for (const row of [...block.rows, ...(block.total ? [block.total] : [])]) {
        const name = row === block.total ? (byDay ? 'Итого за день' : 'Итого') : rowName(row)
        lines.push([...(byDay ? [formatDay(block.day)] : []), name, ...columns.map(column => column.format(row).replace(/\s/g, ''))])
      }
    }
    const csv = lines.map(line => line.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';')).join('\n')
    const link = document.createElement('a')
    link.href = URL.createObjectURL(new Blob(['﻿' + csv], {type: 'text/csv;charset=utf-8'}))
    const from = toApiDate(startDate)
    link.download = `segments_${from ? formatDay(from.slice(0, 10)) : 'all'}.csv`
    link.click()
  }

  const setPeriod = (from: Date | null, to: Date | null) => {
    setStartDate(from)
    setEndDate(to)
  }

  const header = (key: string, title: string) => (
    <th
      key={key}
      onClick={() => setSort({key, desc: sort.key === key ? !sort.desc : key !== 'segment'})}
      style={{cursor: "pointer", padding: 6, whiteSpace: "nowrap"}}>
      {title}{sort.key === key ? (sort.desc ? ' ↓' : ' ↑') : ''}
    </th>
  )

  return (
    <div style={{display: "flex", flex: 1, flexDirection: "column", overflow: "auto", paddingRight: 20, paddingLeft: 20}}>
      <div style={{display: "flex", flexDirection: "row", flexWrap: "wrap", paddingTop: 20, alignItems: "center", gap: 16}}>
        <div style={{display: "flex", alignItems: "center", minWidth: 280}}>
          <div style={filterLabel}>Проект:</div>
          <div style={{flex: 1}}>
            <Select isMulti placeholder="Все проекты" value={selectProjects} options={projectsOptions}
                    onChange={(items: any) => setSelectProjects(items || [])}/>
          </div>
        </div>
        <div style={{display: "flex", alignItems: "center", minWidth: 280}}>
          <div style={filterLabel}>Операторы:</div>
          <div style={{flex: 1}}>
            <Select isMulti placeholder="Все" value={selectOperators} options={operatorsOptions}
                    onChange={(items: any) => setSelectOperators(items || [])}/>
          </div>
        </div>
        <div style={{display: "flex", alignItems: "center", minWidth: 360, flex: 1}}>
          <div style={filterLabel}>Сегменты:</div>
          <div style={{flex: 1}}>
            <Select isMulti placeholder="Все" value={selectSegments} options={segmentsOptions}
                    onChange={(items: any) => setSelectSegments(items || [])}/>
          </div>
        </div>
      </div>

      <div style={{display: "flex", flexDirection: "row", flexWrap: "wrap", paddingTop: 16, alignItems: "center", gap: 8}}>
        <div style={filterLabel}>Звонки с:</div>
        <DateTimePicker onChange={(date: any) => setStartDate(date ? new Date(date) : null)} value={startDate} disableClock={true}/>
        <div style={{...filterLabel, marginLeft: 10}}>по:</div>
        <DateTimePicker onChange={(date: any) => setEndDate(date ? new Date(date) : null)} value={endDate} disableClock={true}/>
        <div style={{display: "flex", marginLeft: 10}}>
          <button style={buttonStyle()} onClick={() => setPeriod(dayStart(0), dayStart(1))}>Сегодня</button>
          <button style={buttonStyle()} onClick={() => setPeriod(dayStart(-1), dayStart(0))}>Вчера</button>
          <button style={buttonStyle()} onClick={() => setPeriod(weekStart(), dayStart(1))}>Эта неделя</button>
          <button style={buttonStyle()} onClick={() => setPeriod(null, null)}>Всё время</button>
        </div>
        <label style={{display: "flex", alignItems: "center", marginLeft: 20}}>
          <input type="checkbox" checked={byDay} onChange={(e) => setByDay(e.target.checked)} style={{marginRight: 6}}/>
          по дням
        </label>
        <button onClick={() => getStat()} style={{...buttonStyle(), marginLeft: 30}}>Применить фильтр</button>
      </div>

      <div style={{display: "flex", flexDirection: "row", flexWrap: "wrap", paddingTop: 16, paddingBottom: 10, alignItems: "center", gap: 8}}>
        <button style={buttonStyle(!rawNames)} onClick={() => setRawNames(false)}>Названия</button>
        <button style={buttonStyle(rawNames)} onClick={() => setRawNames(true)}>Как в базе</button>
        <label style={{display: "flex", alignItems: "center", marginLeft: 20}}>
          <input type="checkbox" checked={showDetails} onChange={(e) => setShowDetails(e.target.checked)} style={{marginRight: 6}}/>
          подробно по исходам
        </label>
        <button style={{...buttonStyle(), marginLeft: 30}} onClick={downloadCsv} disabled={!response}>Скачать CSV</button>
      </div>

      {error && <div style={{color: "red", paddingBottom: 10}}>{error}</div>}

      <div style={{position: 'relative', borderWidth: 1, marginBottom: 10, backgroundColor: "rgb(246 246 251)"}}>
        <table style={{width: "100%"}}>
          <thead>
          <tr style={{borderBottomWidth: 1}}>
            {byDay && <th style={{padding: 6}}>Дата</th>}
            {header('segment', 'Сегмент (сайт)')}
            {columns.map(column => header(column.key, column.title))}
          </tr>
          </thead>
          <tbody>
          {blocks.map(block => (
            <React.Fragment key={block.day ?? 'all'}>
              {block.rows.map(row => (
                <tr key={`${block.day}-${row.segment}`} style={{borderBottomWidth: 1}}>
                  {byDay && <td style={{textAlign: "center"}}>{formatDay(block.day)}</td>}
                  <td style={{padding: 6}}>{rowName(row)}</td>
                  {columns.map(column => (
                    <td key={column.key} style={{textAlign: "center"}}>{column.format(row)}</td>
                  ))}
                </tr>
              ))}
              {block.total && (
                <tr style={{borderBottomWidth: 1, fontWeight: "bold", backgroundColor: "white"}}>
                  {byDay && <td style={{textAlign: "center"}}>{formatDay(block.day)}</td>}
                  <td style={{padding: 6}}>{byDay ? 'Итого за день' : 'Итого'}</td>
                  {columns.map(column => (
                    <td key={column.key} style={{textAlign: "center"}}>{column.format(block.total as Row)}</td>
                  ))}
                </tr>
              )}
            </React.Fragment>
          ))}
          </tbody>
        </table>
        {loading &&
          <div style={{position: "absolute", top: 0, width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: 'center'}}>
            <ClipLoader size={100} color={"black"}/>
          </div>
        }
      </div>
      <div style={{color: "#555", fontSize: 13, paddingBottom: 20}}>
        По каждому номеру берётся лучший исход за период. Живой разговор — лид, перезвон, негатив, сорвался или разговор
        без статуса; «Абонент сбросил звонок» и «Тишина» разговором не считаются. «В базе» — все номера сегмента в выбранных
        проектах. Номер с несколькими сайтами считается в каждом, в «Итого» — один раз.
      </div>
    </div>
  )
}
